import $ from 'jquery';
import Backbone from 'backbone';
import _ from 'underscore';
import DropDownMenu from './DropDownMenu.js';


/**
 * @typedef {Backbone.ViewOptions} DropDownBox.Options
 *
 * @property {String} [position='start top'] The position of the DropDownMenu relative to the DropDownBox.<br />The value consists on 'HORZ VERT' while HORZ can be start/center/end and VERT can be top/center/bottom.<br />RTL for start/end is detected automatically.<br />The "anchor" inside the view is attached to the "position" that was calculated on the "target".
 * @property {String} [anchor='start bottom'] The anchor of the DropDownMenu relative to the DropDownBox.<br />The value consists on 'HORZ VERT' while HORZ can be start/center/end and VERT can be top/center/bottom.<br />RTL for start/end is detected automatically.<br />The "anchor" inside the view is attached to the "position" that was calculated on the "target".
 * @property {{x: number, y: number}} [offset={{x: 0, y: 0}}] An absolute offset to append to the position of the DropDownMenu
 * @property {String} [name=''] The name to use if contained in a FORM
 * @property {String} [defaultText=''] The text to show if nothing is selected
 * @property {Boolean} [itemBlurDelay=300] @see {DropDownMenu.Options.itemBlurDelay}
 * @property {Boolean} [capturesFocus=false] @see {DropDownMenu.Options.capturesFocus}
 * @property {Boolean} [multi=false] @see {DropDownMenu.Options.multi}
 * @property {Boolean} [allowEmpty=false] Is an empty value allowed, or should we automatically select the first item?
 * */

/** @type {DropDownBox.Options} */
let defaultOptions = {
    position: 'start top',
    anchor: 'start bottom',
    offset: {x: 0, y: 0},
    name: '',
    defaultText: '',
    capturesFocus: false,
    multi: false,
    allowEmpty: false,
};

let KeyCodes = {
    /** @let */PAGE_UP: 33,
    /** @let */PAGE_DOWN: 34,
    /** @let */UP: 38,
    /** @let */DOWN: 40,
    /** @let */LEFT: 37,
    /** @let */RIGHT: 39,
    /** @let */ENTER: 13,
    /** @let */TAB: 9,
    /** @let */ESCAPE: 27,
    /** @let */SPACE: 32,
};

/**
 * @class DropDownBox
 * @extends Backbone.View
 * */
let DropDownBox = Backbone.View.extend(
    /** @lends DropDownMenu.prototype */
    {

        tagName: 'span',
        className: 'dropdown-box',
        _template: _.template('<span class="dropdown-box-label"></span><span class="dropdown-box-down"></span><input type="text" value="" tabindex="-1" />'),

        /**
         * @constructs
         * @param {DropDownBox.Options?} options
         * @returns {DropDownBox}
         */
        initialize: function (options) {

            let that = this;

            /** @type {DropDownBox.Options} */
            this.options = options = Object.assign({}, defaultOptions, options);

            this.$el.data('view', this); // Enable access to the view instance using jQuery

            this.$el.html(this._template()).attr('tabindex', 0);

            this.$text = this.$el.find('.dropdown-box-label');

            let $input = this.$input = this.$el.find('input').css({
                width: 0, height: 0,
                outline: 0, border: 0,
                background: 'transparent',
                margin: 0, padding: 0,
            });

            this._isMulti = !!options.multi;

            if (options.defaultText) {
                this.$text.text(options.defaultText);
            }

            if (options.name) {
                $input.attr('name', options.name || '');
            } else {
                $input.removeAttr('name');
            }

            let dropdownOptions = _.pick(options, ['itemBlurDelay', 'capturesFocus', 'multi']);
            let addClasses = (this.className || '').split(/ /g);
            for (let i = 0; i < addClasses.length; i++) {
                if (addClasses[i] === '') {
                    addClasses.splice(i--, 1);
                    continue;
                }
                addClasses[i] = DropDownMenu.prototype.className + '-' + addClasses[i];
            }
            dropdownOptions.className = DropDownMenu.prototype.className + ' ' + addClasses.join(' '); // i.e. 'dropdown-menu dropdown-menu-dropdown-box'

            dropdownOptions.itemTemplate = options.itemTemplate;

            this._dropdown = new DropDownMenu(dropdownOptions);

            this._boundRepositionAndResize = function () {
                that.repositionAndResize();
            };
            this._listeningToScroll = [];

            this
                .listenTo(this._dropdown, 'hide', function () {
                    this.$el.removeClass('dropdown-box-active');
                })
                .listenTo(this._dropdown, 'select', this.onSelect)
                .listenTo(this._dropdown, 'check', this.onCheck);

            // Touch support
            let currentTouchId,
                ghostBuster = 0; // Some Android browsers to not support preventDefault(), we need to do catch the ghost events

            let ghostBust = function (event) {
                if ((+new Date - ghostBuster) <= 500) { // Bust the ghost mouse events on old Android browsers
                    event.preventDefault();
                    return true;
                }
                return false;
            };

            this.$el.on('touchstart', function (event) {
                if (currentTouchId) return;
                currentTouchId = event.originalEvent.changedTouches[0].identifier;

                if (that.isDisabled()) {
                    return;
                }

                $(this).mouseenter();

                let onTouchCancel = function () {
                    that._unhookTouchEvents();
                };

                that._unhookTouchEvents = function () {
                    currentTouchId = null;
                    that.$el.off('touchend').off('touchmove').off('touchcancel');
                    that._unhookTouchEvents = null;
                };

                that.toggle();
                event.preventDefault(); // Prevent selection on drop menu
                that.$el.focus(); // Make sure we have the focus, because of preventDefault() prevents that too

                that.$el.on('touchend', function (event) {
                    let touch = _.find(event.originalEvent.changedTouches, function (touch) {
                        return touch.identifier === currentTouchId;
                    });
                    if (!touch) return;

                    ghostBuster = +new Date;

                    event.preventDefault();
                    that._unhookTouchEvents();
                }).on('touchmove', function (event) {
                    event.preventDefault();
                }).on('touchcancel', onTouchCancel);

            });

            let blurEvent = function (/*evt*/) {
                _.delay(function () {
                    let activeElement = document.activeElement;

                    if (!that._dropdown || !that.$el) return;
                    if (that.$el[0] === activeElement) return;
                    if (that.$el.has(activeElement).length) return;
                    if (that._dropdown.$el.has(activeElement).length) return;

                    that.hide();
                });
            };

            this.$el
                .on('blur', blurEvent)
                .on('mousedown', function (event) {
                    if (event.which !== 1) return;

                    if (ghostBust(event)) return;

                    if (that.isDisabled()) {
                        return;
                    }

                    that.toggle();
                    event.preventDefault(); // Prevent selection on drop menu
                    that.$el.focus(); // Make sure we have the focus, because of preventDefault() prevents that too
                })
                .on('keydown keypress', function (evt) {

                    if (that.isDisabled()) {
                        return;
                    }

                    let isVisible = that.isVisible();
                    if (!isVisible && (evt.which === KeyCodes.SPACE || evt.which === KeyCodes.ENTER)) {
                        that.toggle();
                        evt.preventDefault();
                        evt.stopPropagation();
                        return;
                    }

                    if (!that._dropdown.isVisible() && that._selected) {
                        that._dropdown.setFocusedItem(that._selected);
                    }
                    that._dropdown._keydown(event);
                });

            // We have that input mainly to support LABEL tag, and we capture its focus
            this.$el.find('input[type=text]').on('focus', function () {
                that.$el.focus();
            });
        },

        remove: function () {
            if (this._dropdown) {
                this.hide();
                this._dropdown.remove();
                this._dropdown = null;
            }
            this._stopTrackingPresence();
            this.$input = null;
            this.$text = null;
            DropDownBox.__super__.remove.apply(this, arguments);
        },

        /** @private */
        _startTrackingPresence: function () {
            let that = this;

            let isPresent = function () {
                let parent = that.el.parentNode,
                    elementPresent = false;
                while (parent) {
                    if (parent === document.documentElement) {
                        elementPresent = true;
                        break;
                    }
                    parent = parent.parentNode;
                }
                return elementPresent;
            };

            that._stopTrackingPresence();

            that._presenceInt = setInterval(function () {
                if (!isPresent()) {
                    that.hide();
                }
            }, 200);

            return that;
        },

        /** @private */
        _stopTrackingPresence: function () {
            if (this._presenceInt) {
                clearInterval(this._presenceInt);
                this._presenceInt = null;
            }
            return this;
        },

        show: function () {
            let that = this;
            if (!that.isVisible()) {

                that.$el.addClass('dropdown-box-active');
                that._dropdown.prepareShow().$el.show();

                that._startTrackingPresence();

                $(window).off('resize', this._boundRepositionAndResize).on('resize', this._boundRepositionAndResize);

                let $parent = this.$el.parent();
                while ($parent.length) {
                    if ($parent[0].scrollHeight > $parent[0].offsetHeight || $parent[0].scrollWidth > $parent[0].offsetWidth) {
                        if ($parent[0] === document.documentElement) {
                            $parent = $(window);
                        }
                        this._listeningToScroll.push($parent.on('scroll', this._boundRepositionAndResize));
                    }
                    $parent = $parent.parent();
                }
            }

            this.render().repositionAndResize();

            if (that._selected) {
                that._dropdown.setFocusedItem(that._selected);
            }

            return that;
        },

        toggle: function () {
            if (this.isVisible()) {
                this.hide();
            } else {
                this.show();
            }
            return this;
        },

        hide: function () {
            if (this._dropdown.isVisible()) {
                this._dropdown.hide();
                this.trigger('hide');
            }

            this._stopTrackingPresence();

            $(window).off('resize', this._boundRepositionAndResize);

            if (this._listeningToScroll.length) {
                for (let i = 0; i < this._listeningToScroll.length; i++) {
                    this._listeningToScroll[i].off('scroll', this._boundRepositionAndResize);
                }
                this._listeningToScroll = [];
            }

            return this;
        },

        isVisible: function () {
            return this._dropdown.isVisible();
        },

        isEnabled: function () {
            return !this.isDisabled();
        },

        isDisabled: function () {
            return this.$el.hasClass('disabled');
        },

        enable: function () {

            if (arguments.length && !arguments[0]) {
                return this.disable();
            }

            if (this.el.getAttribute('tabindex') == null && this._enabledTabIndex != null) {
                this.el.setAttribute('tabindex', this._enabledTabIndex);
                delete this._enabledTabIndex;
            }

            this.$el.removeClass('disabled');

            return this;
        },

        disable: function () {

            if (arguments.length && !arguments[0]) {
                return this.enable();
            }

            if (this.el.getAttribute('tabindex') != null) {
                this._enabledTabIndex = this.el.getAttribute('tabindex');
                this.el.removeAttribute('tabindex');
            }

            this.$el.addClass('disabled');

            return this;
        },

        repositionAndResize: function () {

            this._dropdown.repositionAndResize({
                $target: this.$el,
                offset: this.options.offset,
                anchor: this.options.anchor,
                position: this.options.position,
                updateWidth: true,
            });

            return this;
        },

        onSelect: function (event) {
            let item = event.item;

            if (!item) {
                this.hide();
                return;
            }
            let shouldTrigger = this._selected !== item;
            this._selected = item;
            if (!this._isMulti) {
                this._updateInput();
                if (shouldTrigger) {
                    this.trigger('change', item, true);
                    this.$input.change();
                }
                this.hide();
            }
        },

        onCheck: function (event) {

            let item = event.item,
                checked = item.checked;

            if (!this._checked) {
                this._checked = [];
            }
            if (checked) {
                this._checked.push(item);
            } else {
                for (let i = 0; i < this._checked.length; i++) {
                    if (this._checked[i].value === item.value) {
                        this._checked.splice(i, 1);
                        i--;
                    }
                }
            }
            this._updateInput();

            if (this._dropdown.isVisible()) {
                // That's in case the CSS allows the drop down box to grow/shrink in height
                this.repositionAndResize();
            }

            this.trigger('change', item, checked);
            this.$input.change();
        },

        addItem: function (value, label, selected, atIndex) {
            this._dropdown.addItem(value, label, selected, atIndex);

            if (!this._isMulti) {
                if (selected) {
                    this._selected = {value: value, label: label};
                    this._updateInput();
                }

                if (!this._selected && !this.options.allowEmpty && this._dropdown.itemCount() > 0) {
                    this._selected = this._dropdown.itemAtIndex(0);
                    this._selected = this._dropdown.itemAtIndex(0);
                    this._updateInput();
                }
            }

            return this;
        },

        /**
         * Adds items to the menu and renders
         * @param {Array<{
         *  label: String,
         *  short_label: String,
         *  value: *,
         *  checked: Boolean?,
         *  group: Boolean=false,
         *  child: Boolean=false,
         *  nocheck: Boolean=false,
         *  nointeraction: Boolean=false
         * }>} itemsToAdd The items to add. These are copied.
         * @param atIndex The index to insert at (or -1)
         * @returns {DropDownBox}
         */
        addItems: function (itemsToAdd, atIndex) {
            this._dropdown.addItems(itemsToAdd, atIndex);

            if (!this._isMulti) {
                if (!this._selected && !this.options.allowEmpty && this._dropdown.itemCount() > 0) {
                    this._selected = this._dropdown.itemAtIndex(0);
                    this._updateInput();
                }
            }

            return this;
        },

        removeItem: function (value, label) {
            this._dropdown.removeItem(value, label);
            if (this._selected && (this._selected.value === value || (value == null && this._selected.label === label))) {
                this._selected = null;
                this._updateInput();
            }
            if (this._checked) {
                let hasUpdates = false;
                for (let i = 0; i < this._checked.length; i++) {
                    if (this._checked[i].value === value || (value == null && this._checked[i].label === label)) {
                        this._checked.splice(i, 1);
                        i--;
                        hasUpdates = true;
                    }
                }
                if (hasUpdates) {
                    this._updateInput();
                }
            }
            return this;
        },

        updateItem: function (value, label) {
            this._dropdown.updateItem(value, label);
            if (this._selected && this._selected.value === value) {
                this._selected.label = label;
                this._updateInput();
            }
            return this;
        },

        removeAllItems: function () {
            this._dropdown.removeAllItems();
            this._checked = [];
            this._selected = null;
            this.$text.text('');
            return this;
        },

        getItems: function () {
            return this._dropdown.items();
        },

        getItemCount: function () {
            return this._dropdown.itemCount();
        },

        value: function () {
            let that = this;

            if (arguments.length) {

                let oldChecks = that._checked;

                if (that._isMulti) {

                    that._checked = [];

                    let items = _.isArray(arguments[0]) ? arguments[0] : Array.prototype.slice.call(arguments, 0);
                    that._dropdown.setCheckedValues(items);

                    that._checked = that._dropdown.getCheckedItems();

                } else {
                    that._selected = that._dropdown.itemDataByValue(arguments[0]);
                    if (!that._selected && !that.options.allowEmpty) {
                        that._selected = that._dropdown.itemAtIndex(0);
                    }
                    if (that._dropdown.isVisible()) {
                        that._dropdown.setFocusedItem(that._selected);
                    } else {
                        that._dropdown.setFocusedItem(null);
                    }
                }

                that._updateInput();

                if (arguments[1]) {
                    if (that._isMulti) {

                        // These stayed the same
                        let sameChecks = oldChecks.filter(a => that._checked.some(b => a.value === b.value));

                        // Old checks to trigger
                        oldChecks.forEach(item => {
                            if (sameChecks.some(b => item.value === b.value))
                                return;

                            that.trigger('change', item, false);
                        });

                        // New checks to trigger
                        this._checked.forEach(item => {
                            if (sameChecks.some(b => item.value === b.value))
                                return;

                            that.trigger('change', item, true);
                        });

                    } else {
                        that.trigger('change', that._selected, true);
                    }

                    that.$input.change();
                }

                return that;
            } else {
                if (that._isMulti) {
                    return (that._checked || []).map(x => x.value);
                } else {
                    return that._selected ? that._selected.value : null;
                }
            }
        },

        val: function () {
            // jQuery val style compatibility
            return this.value.apply(this, arguments);
        },

        text: function () {
            if (arguments.length) {
                this.$text.text(arguments[0]);
            } else {
                return this.$text.text();
            }
        },

        _updateInput: function () {
            if (this._isMulti) {
                let label = '';
                for (let i = 0; i < this._checked.length; i++) {
                    if (i > 0) {
                        label += ', ';
                    }
                    label += this._checked[i].short_label || this._checked[i].label;
                }
                this.$text.text(this._checked.length ? label : (this.options.defaultText || ''));
            } else {
                if (this._selected) {
                    this.$text.text(this._selected.label);
                } else {
                    this.$text.text(this.options.defaultText || '');
                }
            }
            this.$input.val(this.value());
            return this;
        },

    },
);

export default DropDownBox;
